import { useContext } from 'react';
import { createOrUnlockAccountData } from '../../create-account/createAccountContext';
import Local from '../classes/local';
import { ApiError } from '../functions/getApiErrorMessage';
import getDateStringFromObject from '../functions/getDateStringFromObject';
import { stripInvalidMbiChars } from '../functions/stripInvalidMbiChars';
import { DateObject } from '../objects/models/date.field.value';
import useAxios from './useAxios';

interface Step2Data {
    dateOfBirth: string
    effectiveDate: string
    effectiveDatePart: string
    lastName: string
    mbi: string
    zipCodeOrCity: string
    email?: string
}

export interface Step3Data extends Step2Data {
    challengeQuestionAndAnswers: {
        answer: string
        question: string
    }[]
    lang: string
    password: string
    username: string
    promptForMFAEnrollment?: boolean
}

export interface CreateUnlockAccountResponse {
    status: string
    code: number
    errors?: ApiError[]
}

export default function useCreateAccountApi() {
    const axios = useAxios();
    const { data } = useContext(createOrUnlockAccountData);

    const step1 = async (mbi: string, effectiveDate: DateObject, effectiveDatePart: 'A' | 'B') => {
        return axios.call<CreateUnlockAccountResponse>(
            'POST',
            '/account/api/create-account/step/1',
            {
                effectiveDate: getDateStringFromObject(effectiveDate),
                effectiveDatePart: effectiveDatePart.toLowerCase(),
                mbi: stripInvalidMbiChars(mbi),
            },
        );
    };

    const step2 = async (lastName: string, dateOfBirth: DateObject, zipCodeOrCity: string, email: string) => {
        const requestData: Step2Data = {
            dateOfBirth: getDateStringFromObject(dateOfBirth),
            effectiveDate: getDateStringFromObject(data.coverageStartDate),
            effectiveDatePart: data.part.toLowerCase(),
            lastName,
            mbi: stripInvalidMbiChars(data.medicareNumber),
            zipCodeOrCity,
        };
        if (email) {
            requestData.email = email;
        }
        return axios.call(
            'POST',
            '/account/api/create-account/step/2',
            requestData,
        );
    };

    const step3 = async (username: string, password: string, challengeQuestion: string, challengeAnswer: string, promptForMFAEnrollment?: boolean) => {
        const language = Local.getItem('language', 'English');
        const submittedData: Step3Data = {
            challengeQuestionAndAnswers: [{
                answer: challengeAnswer,
                question: challengeQuestion,
            }],
            dateOfBirth: getDateStringFromObject(data.dateOfBirth),
            effectiveDate: getDateStringFromObject(data.coverageStartDate),
            effectiveDatePart: data.part.toLowerCase(),
            lang: language === 'English' ? 'en' : 'es',
            lastName: data.lastNameWithSuffix,
            mbi: stripInvalidMbiChars(data.medicareNumber),
            password: password,
            username,
            zipCodeOrCity: data.zipCodeOrCity,
        };
        if(promptForMFAEnrollment !== undefined) {
            submittedData.promptForMFAEnrollment = promptForMFAEnrollment;
        }
        if (data.emailAddress !== '')
            submittedData.email = data.emailAddress;
        return axios.call(
            'POST',
            '/account/api/create-account/step/3',
            submittedData,
        );
    };

    return {
        step1,
        step2,
        step3,
    };
}
